import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import ValidationMessage from 'components/validation/ValidationMessage';
import DateField from 'components/fields/DateField';
import TimeField from 'components/fields/TimeField';

import SharedTextFieldInput from 'components/fields/shared/TextFieldInput';
import ValidateFile from 'components/fields/shared/Message/components/ValidateFile';
import ValidateText from 'components/fields/shared/Message/components/ValidateText';

import useFormBinderDisabled from 'hooks/useFormBinderDisabled';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import { FILE_TYPE } from 'variables/constants';

import { useCarriers } from 'hooks/use-carriers-hook';

import { useMarketApprovalResolver } from './hooks';
import {
  getHTMLId,
  useResolverModal,
  parseDateString,
  BINDING_AUTHORITY_MESSAGE,
  MARKET_MISMATCH_MESSAGE,
} from './resolverUtils';
import type { ValidationResolverProps } from './types';

export interface Validation032ResolverProps extends ValidationResolverProps {}

const VALIDATION_ID = '032';
const FORM_STATE_KEY = 'autoPersonalForm';

const Validation032Resolver = ({
  section,
  itemKey: propItemKey,
  compact = false,
}: Validation032ResolverProps) => {
  const { uuid } = useParams();
  const isBinderDisabled = useFormBinderDisabled(FORM_STATE_KEY);
  const isMarketApprovalEmailEmlMsgEnabled = useFeatureFlag(
    'enable-market-approval-email-eml-msg',
  );
  const {
    isOpen: isCompactOpen,
    onOpen: onCompactOpen,
    onClose: onCompactClose,
  } = useDisclosure();

  const itemKey = propItemKey || 'global';

  const { state, handlers, fieldIds, alertMessage, getCompactAlertStatus } =
    useMarketApprovalResolver({
      validationId: VALIDATION_ID,
      section,
      itemKey,
      formStateKey: FORM_STATE_KEY,
      showLOEOption: true,
      marketApprovalFileType: FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO.type,
      loeFileType: FILE_TYPE.TYPE_032.type,
      loeFileLabel: FILE_TYPE.TYPE_032.text,
      marketRequired: true,
    });

  const { carrierOptions } = useCarriers();

  const htmlId = getHTMLId(VALIDATION_ID, itemKey);

  // Use shared modal hook for hash-based navigation
  const { handleModalClose } = useResolverModal(
    compact,
    Object.values(fieldIds),
    htmlId,
    onCompactOpen,
    onCompactClose,
  );

  if (!state.show) return null;

  // Compact mode: show button that opens modal
  if (compact) {
    return (
      <>
        {/* Anchor divs for mandatory field navigation */}
        <div id={htmlId}></div>
        <div id={fieldIds.resolution}></div>
        <div id={fieldIds.approvalMethod}></div>
        <div id={fieldIds.remarks}></div>
        <div id={fieldIds.date}></div>
        <div id={fieldIds.time}></div>
        <div id={fieldIds.underwriter}></div>
        <div id={fieldIds.market}></div>
        <Tooltip placement="top" label={alertMessage}>
          <Alert status={getCompactAlertStatus()} mt="10px" fontSize="14px">
            <Button
              onClick={() => onCompactOpen()}
              w="100%"
              whiteSpace="normal"
            >
              <AlertIcon />
              Add remark
            </Button>
          </Alert>
        </Tooltip>
        <Modal
          isOpen={isCompactOpen}
          onClose={handleModalClose}
          size="4xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Market Approval Resolution</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb="10px">
                <Validation032Resolver
                  section={section}
                  itemKey={itemKey}
                  compact={false}
                />
              </Box>
              <Button
                variant="brand"
                onClick={handleModalClose}
                mb="15px"
                float="right"
              >
                Close
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Box>
      {/* Anchor divs for mandatory field navigation */}
      <div id={htmlId}></div>
      <div id={fieldIds.resolution}></div>
      <div id={fieldIds.approvalMethod}></div>
      <div id={fieldIds.remarks}></div>
      <div id={fieldIds.date}></div>
      <div id={fieldIds.time}></div>
      <div id={fieldIds.underwriter}></div>
      <div id={fieldIds.market}></div>
      <Alert status="warning" mt="10px" fontSize="14px">
        <AlertIcon />
        <ValidationMessage message={alertMessage} />
      </Alert>

      <Box mt="15px" p="15px" bg="gray.50" borderRadius="md">
        {/* First level: Resolution Method (LOE vs Market Approval) */}
        <FormControl mb="15px" id={fieldIds.resolution}>
          <RadioGroup
            value={state.resolutionType || ''}
            onChange={handlers.handleResolutionTypeChange}
            isDisabled={isBinderDisabled}
          >
            <Stack direction="row" spacing={5}>
              <Radio value="loe">Letter of Experience</Radio>
              <Radio value="market_approval">Market Approval</Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        {/* LOE Path */}
        {state.resolutionType === 'loe' && state.remark && (
          <VStack spacing={4} align="stretch">
            <Box id={fieldIds.remarks}>
              <SharedTextFieldInput
                label="Remarks"
                value={state.remark.text || ''}
                onChange={handlers.handleRemarksChange}
                borderColor={
                  !state.remark.text || state.remark.text.trim().length === 0
                    ? 'red'
                    : 'inherit'
                }
                formStateKey={FORM_STATE_KEY}
                section={section}
              />
            </Box>

            <ValidateText
              uuid={uuid}
              remark={state.remark}
              validationId={VALIDATION_ID}
              itemKey={itemKey}
              formStateKey={FORM_STATE_KEY}
              context={alertMessage}
            />

            {state.file && (
              <ValidateFile
                uuid={uuid}
                file={state.file}
                validationId={VALIDATION_ID}
                itemKey={itemKey}
                formStateKey={FORM_STATE_KEY}
              />
            )}
          </VStack>
        )}

        {/* Market Approval Path */}
        {state.resolutionType === 'market_approval' && (
          <Box>
            {state.remark && (
              <VStack spacing={4} align="stretch" mb="15px">
                <Box id={fieldIds.remarks}>
                  <SharedTextFieldInput
                    label="Remarks"
                    value={state.remark.text || ''}
                    onChange={handlers.handleRemarksChange}
                    borderColor={
                      !state.remark.text ||
                      state.remark.text.trim().length === 0
                        ? 'red'
                        : 'inherit'
                    }
                    formStateKey={FORM_STATE_KEY}
                    section={section}
                  />
                </Box>
                <ValidateText
                  uuid={uuid}
                  remark={state.remark}
                  validationId={VALIDATION_ID}
                  itemKey={itemKey}
                  formStateKey={FORM_STATE_KEY}
                  context={[alertMessage, state.approvalContext]
                    .filter(Boolean)
                    .join(' ')}
                />
              </VStack>
            )}
            <FormControl mb="15px" id={fieldIds.approvalMethod}>
              <FormLabel fontWeight="semibold">Approval Method</FormLabel>
              <RadioGroup
                value={state.approvalMethod || ''}
                onChange={handlers.handleApprovalMethodChange}
                isDisabled={isBinderDisabled}
              >
                <Stack direction="row" spacing={5}>
                  <Radio value="email">I got approval via email</Radio>
                  <Radio value="phone_call">
                    I got approval via phone call
                  </Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            {/* Email path */}
            {state.approvalMethod === 'email' && state.file && (
              <Box>
                <Text fontSize="sm" color="gray.600" mb="10px">
                  {isMarketApprovalEmailEmlMsgEnabled
                    ? 'Upload market approval email as evidence (.eml or .msg only)'
                    : 'Upload market approval email as evidence'}
                </Text>
                <ValidateFile
                  uuid={uuid}
                  file={state.file}
                  validationId={VALIDATION_ID}
                  itemKey={itemKey}
                  formStateKey={FORM_STATE_KEY}
                />
              </Box>
            )}

            {state.showPhoneCallFields && state.remark && (
              <VStack spacing={4} align="stretch" mt="10px">
                <Box>
                  <SharedTextFieldInput
                    label="Summary of Market Conversation"
                    value={state.summaryOfMarketConversationText || ''}
                    onChange={handlers.handleSummaryChange}
                    borderColor={
                      !state.summaryOfMarketConversationText ||
                      state.summaryOfMarketConversationText.trim().length === 0
                        ? 'red'
                        : 'inherit'
                    }
                    formStateKey={FORM_STATE_KEY}
                    section={section}
                  />
                </Box>

                <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                  <Box flex={1} id={fieldIds.date}>
                    <DateField
                      label="Approval Date"
                      date={parseDateString(state.approvalDate)}
                      onChange={handlers.handleDateChange}
                      required
                      isDisabled={isBinderDisabled}
                      htmlID={fieldIds.date}
                    />
                    {state.approvalOutsideBindingAuthority && (
                      <Text fontSize="sm" color="red.500" mt="5px">
                        {BINDING_AUTHORITY_MESSAGE}
                      </Text>
                    )}
                  </Box>
                  <Box flex={1} id={fieldIds.time}>
                    <TimeField
                      label="Approval Time"
                      value={state.approvalTime || ''}
                      onChange={handlers.handleTimeChange}
                      required
                      isDisabled={isBinderDisabled}
                      htmlID={fieldIds.time}
                    />
                  </Box>
                </Stack>

                <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                  <FormControl isRequired flex={1} id={fieldIds.underwriter}>
                    <FormLabel>Underwriter Name</FormLabel>
                    <Input
                      variant="main"
                      value={state.underwriterName || ''}
                      onChange={handlers.handleUnderwriterNameChange}
                      disabled={isBinderDisabled}
                      borderColor={
                        !state.underwriterName ||
                        state.underwriterName.trim().length === 0
                          ? 'red'
                          : 'inherit'
                      }
                    />
                  </FormControl>

                  <FormControl isRequired flex={1} id={fieldIds.market}>
                    <FormLabel>Market</FormLabel>
                    <Select
                      variant="main"
                      value={state.market || ''}
                      onChange={(e) =>
                        handlers.handleMarketChange(e.target.value)
                      }
                      disabled={isBinderDisabled}
                    >
                      <option value="">Select market...</option>
                      {carrierOptions.map((opt) => (
                        <option key={opt.value} value={opt.label}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                    {state.marketMismatch && (
                      <Text fontSize="sm" color="red.500" mt="5px">
                        {MARKET_MISMATCH_MESSAGE}
                      </Text>
                    )}
                  </FormControl>
                </Stack>

                <FormControl>
                  <FormLabel>Approval Number (Optional)</FormLabel>
                  <Input
                    variant="main"
                    value={state.approvalNumber || ''}
                    onChange={handlers.handleApprovalNumberChange}
                    disabled={isBinderDisabled}
                  />
                </FormControl>
              </VStack>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Validation032Resolver;
